import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import {
  FiPlus, FiGrid, FiList, FiEdit2, FiCopy, FiTrash2, FiExternalLink,
  FiSearch, FiFilter, FiAlertCircle, FiImage, FiRefreshCw, FiGlobe,
  FiCheck, FiLoader, FiCheckCircle, FiXCircle, FiGithub,
} from 'react-icons/fi';
import supabase from '../../supabase/supabase';
import TopNav from '../builder/TopNav';
import { createNetlifySite, deleteNetlifySite, nameToNetlifySlug } from '../../utils/netlify';

// ── Cache helpers ─────────────────────────────────────────────────────────────
const CACHE_VERSION = 'v1';
function cacheKey(userId) { return `sh_sites_${userId}_${CACHE_VERSION}`; }
function readCache(userId) {
  try { return JSON.parse(localStorage.getItem(cacheKey(userId)) || 'null'); } catch { return null; }
}
function writeCache(userId, data) {
  try { localStorage.setItem(cacheKey(userId), JSON.stringify(data)); } catch {}
}

// ── Skeleton Card ─────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bg-[#1e2228] rounded-2xl border border-[#2c313a] overflow-hidden animate-pulse">
      <div className="aspect-video bg-[#2c313a]" />
      <div className="p-4 space-y-2">
        <div className="h-4 bg-[#2c313a] rounded-lg w-3/4" />
        <div className="h-3 bg-[#2c313a] rounded-lg w-1/2" />
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <tr className="border-b border-[#2c313a] animate-pulse">
      <td className="px-6 py-4"><div className="w-16 h-10 bg-[#2c313a] rounded-lg" /></td>
      <td className="px-6 py-4"><div className="h-4 bg-[#2c313a] rounded-lg w-32" /></td>
      <td className="px-6 py-4"><div className="h-4 bg-[#2c313a] rounded-lg w-16" /></td>
      <td className="px-6 py-4"><div className="h-4 bg-[#2c313a] rounded-lg w-20" /></td>
      <td className="px-6 py-4"><div className="h-4 bg-[#2c313a] rounded-lg w-24" /></td>
      <td className="px-6 py-4"><div className="h-4 bg-[#2c313a] rounded-lg w-20" /></td>
    </tr>
  );
}

// ── Netlify Status Badge ──────────────────────────────────────────────────────
const NetlifyStatusBadge = ({ site, size = 'md' }) => {
  const hasSubdomain = !!site.netlify_subdomain;
  const hasSiteId = !!site.netlify_site_id;
  if (!hasSubdomain && !hasSiteId) return null;
  const isSuccess = hasSubdomain && hasSiteId;
  if (size === 'sm') {
    return (
      <div title={isSuccess ? `Netlify: ${site.netlify_subdomain}` : 'Netlify provisioning failed'}
        className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 shadow-sm ${isSuccess ? 'bg-green-500 shadow-green-500/50' : 'bg-red-500 shadow-red-500/50'}`}>
        <SafeIcon icon={isSuccess ? FiCheck : FiXCircle} className="w-2.5 h-2.5 text-white" />
      </div>
    );
  }
  return (
    <div className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${isSuccess ? 'bg-green-500/15 border-green-500/40 text-green-400' : 'bg-red-500/15 border-red-500/40 text-red-400'}`}>
      <SafeIcon icon={isSuccess ? FiCheckCircle : FiXCircle} className="w-3 h-3" />
      <span>{isSuccess ? 'Netlify Live' : 'Netlify Failed'}</span>
    </div>
  );
};

// ── Subdomain Badge ───────────────────────────────────────────────────────────
const SubdomainBadge = ({ site }) => {
  const [copied, setCopied] = useState(false);
  if (!site.netlify_subdomain) return null;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`https://${site.netlify_subdomain}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-center space-x-1.5 mt-1">
      <SafeIcon icon={FiGlobe} className="w-3 h-3 text-[#c8f542]" />
      <span className="text-[10px] text-[#c8f542] font-bold truncate max-w-[160px]">{site.netlify_subdomain}</span>
      <button onClick={handleCopy} title="Copy subdomain" className="text-gray-500 hover:text-[#c8f542] transition-colors">
        <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3 h-3" />
      </button>
    </div>
  );
};

// ── Site Thumbnail Card ───────────────────────────────────────────────────────
const SiteThumbnail = ({ site, onEdit, onDuplicate, onDelete }) => {
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const hasNetlify = !!site.netlify_site_id;
  const netlifySuccess = hasNetlify && !!site.netlify_subdomain;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -4 }}
      className="bg-[#1e2228] rounded-2xl border border-[#2c313a] overflow-hidden group hover:shadow-xl hover:shadow-black/40 transition-all flex flex-col"
    >
      <div className="aspect-video relative overflow-hidden bg-[#16191f]">
        {site.banner_url ? (
          <img src={site.banner_url} alt={site.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <SafeIcon icon={FiGrid} className="w-12 h-12 text-[#c8f542]/20" />
          </div>
        )}
        <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider z-10 shadow-md ${site.type === 'website' ? 'bg-[#c8f542] text-black' : 'bg-purple-500 text-white'}`}>
          {site.type}
        </div>
        {hasNetlify && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
            className="absolute top-3 right-3 z-10"
            title={netlifySuccess ? `✅ Netlify subdomain: ${site.netlify_subdomain}` : '❌ Netlify provisioning failed'}
          >
            <div className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-[10px] font-black border shadow-lg ${netlifySuccess ? 'bg-green-500/90 border-green-400/60 text-white shadow-green-500/30' : 'bg-red-500/90 border-red-400/60 text-white shadow-red-500/30'}`}>
              <SafeIcon icon={netlifySuccess ? FiCheckCircle : FiXCircle} className="w-3 h-3" />
              <span>{netlifySuccess ? 'Live' : 'Failed'}</span>
            </div>
          </motion.div>
        )}
        {site.netlify_subdomain && (
          <div className="absolute bottom-3 left-3 right-3 flex items-center space-x-1.5 bg-black/60 backdrop-blur-sm rounded-lg px-2.5 py-1.5 z-10">
            <SafeIcon icon={FiGlobe} className="w-3 h-3 text-[#c8f542] shrink-0" />
            <span className="text-[10px] text-[#c8f542] font-bold truncate">{site.netlify_subdomain}</span>
          </div>
        )}
        <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2 z-20">
          <button onClick={(e) => { e.stopPropagation(); onEdit(site); }} className="p-2.5 bg-[#c8f542] text-black rounded-xl hover:bg-[#d4ff50] transition-all font-bold" title="Edit">
            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
          </button>
          {site.netlify_subdomain && (
            <a href={`https://${site.netlify_subdomain}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="p-2.5 bg-[#c8f542]/20 text-[#c8f542] border border-[#c8f542]/40 rounded-xl hover:bg-[#c8f542]/30 transition-all" title="Open live site">
              <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
            </a>
          )}
          <button onClick={(e) => { e.stopPropagation(); onDuplicate(site); }} className="p-2.5 bg-[#2c313a] text-white rounded-xl hover:bg-[#383e49] transition-all" title="Duplicate">
            <SafeIcon icon={FiCopy} className="w-4 h-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(site); }} className="p-2.5 bg-red-500/80 text-white rounded-xl hover:bg-red-500 transition-all" title="Delete">
            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-white truncate flex-1 mr-2">{site.name}</h3>
            {hasNetlify && <NetlifyStatusBadge site={site} size="sm" />}
          </div>
          <SubdomainBadge site={site} />
        </div>
        <p className="text-xs text-gray-500 mt-2">Updated {formatDate(site.updated_at)}</p>
      </div>
    </motion.div>
  );
};

// ── List Row ──────────────────────────────────────────────────────────────────
const ListRow = ({ site, onEdit, onDuplicate, onDelete }) => {
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const hasNetlify = !!site.netlify_site_id;
  const netlifySuccess = hasNetlify && !!site.netlify_subdomain;
  return (
    <tr className="hover:bg-[#1e2228] transition-colors border-b border-[#2c313a]">
      <td className="px-6 py-4">
        <div className="w-16 h-10 rounded-lg overflow-hidden bg-[#16191f] border border-[#2c313a] flex items-center justify-center relative">
          {site.banner_url
            ? <img src={site.banner_url} alt={site.name} className="absolute inset-0 w-full h-full object-cover" />
            : <SafeIcon icon={FiImage} className="w-5 h-5 text-gray-600" />}
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="font-medium text-white">{site.name}</div>
        {site.netlify_subdomain && (
          <div className="flex items-center space-x-1 mt-0.5">
            <SafeIcon icon={FiGlobe} className="w-3 h-3 text-[#c8f542]" />
            <a href={`https://${site.netlify_subdomain}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#c8f542] hover:underline font-bold" onClick={(e) => e.stopPropagation()}>
              {site.netlify_subdomain}
            </a>
          </div>
        )}
      </td>
      <td className="px-6 py-4">
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${site.type === 'website' ? 'bg-[#c8f542]/20 text-[#c8f542]' : 'bg-purple-500/20 text-purple-400'}`}>
          {site.type}
        </span>
      </td>
      <td className="px-6 py-4">
        {hasNetlify ? (
          <span className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${netlifySuccess ? 'bg-green-500/15 border-green-500/40 text-green-400' : 'bg-red-500/15 border-red-500/40 text-red-400'}`}>
            <SafeIcon icon={netlifySuccess ? FiCheckCircle : FiXCircle} className="w-3 h-3" />
            <span>{netlifySuccess ? 'Netlify Live' : 'Netlify Failed'}</span>
          </span>
        ) : (
          <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-[#2c313a] text-gray-400">Draft</span>
        )}
      </td>
      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(site.updated_at)}</td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <button onClick={() => onEdit(site)} className="p-2 text-gray-400 hover:text-[#c8f542] hover:bg-[#c8f542]/10 rounded-lg transition-all">
            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
          </button>
          {site.netlify_subdomain && (
            <a href={`https://${site.netlify_subdomain}`} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-[#c8f542] hover:bg-[#c8f542]/10 rounded-lg transition-all">
              <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
            </a>
          )}
          <button onClick={() => onDuplicate(site)} className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all">
            <SafeIcon icon={FiCopy} className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(site)} className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all">
            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// ── Creation Toast ────────────────────────────────────────────────────────────
const CreationToast = ({ result, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 6000); return () => clearTimeout(t); }, [onClose]);
  if (!result) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.95 }}
      className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-[200] flex items-start space-x-3 px-5 py-4 rounded-2xl border shadow-2xl max-w-sm w-full ${result.success ? 'bg-[#111318] border-green-500/40 shadow-green-500/20' : 'bg-[#111318] border-red-500/40 shadow-red-500/20'}`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${result.success ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
        <SafeIcon icon={result.success ? FiCheckCircle : FiXCircle} className={`w-5 h-5 ${result.success ? 'text-green-400' : 'text-red-400'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-black ${result.success ? 'text-green-400' : 'text-red-400'}`}>
          {result.success ? '🚀 Netlify Subdomain Created!' : '⚠️ Netlify Provisioning Failed'}
        </p>
        <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
          {result.success ? result.subdomain : 'Site was saved but no subdomain was assigned.'}
        </p>
      </div>
      <button onClick={onClose} className="text-gray-600 hover:text-gray-300 transition-colors shrink-0">
        <SafeIcon icon={FiXCircle} className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

// ── Syncing Indicator ─────────────────────────────────────────────────────────
function SyncIndicator({ isRefreshing }) {
  return (
    <AnimatePresence>
      {isRefreshing && (
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
          className="flex items-center space-x-1.5 text-[10px] text-gray-500 font-bold uppercase tracking-widest"
        >
          <SafeIcon icon={FiRefreshCw} className="w-3 h-3 animate-spin" />
          <span>Syncing…</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
const SitesFunnelsDashboard = ({
  onClose, onOpenWebsite, onOpenBuilder, onOpenStore,
  menuStyles, onMenuStylesChange, activeWebsiteName, userId,
}) => {
  const [websites, setWebsites] = useState(() => (userId ? readCache(userId) || [] : []));
  const [isFirstLoad, setIsFirstLoad] = useState(() => !(userId && readCache(userId)));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteType, setNewSiteType] = useState('website');
  const [githubRepo, setGithubRepo] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [netlifyStatus, setNetlifyStatus] = useState('');

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [creationToast, setCreationToast] = useState(null);

  const isMounted = useRef(true);
  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);

  // Fetch from Supabase — silent if cache already shown, spinner only on first load
  const fetchWebsites = useCallback(async ({ silent = false } = {}) => {
    if (!userId) { setWebsites([]); setIsFirstLoad(false); return; }
    if (!silent) setIsRefreshing(true);
    setLoadError(null);
    try {
      const { data, error } = await supabase
        .from('websites_1772750000000')
        .select('*, banner_url')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });
      if (!isMounted.current) return;
      if (error) throw error;
      const fresh = data || [];
      writeCache(userId, fresh);
      setWebsites(fresh);
    } catch (err) {
      if (!isMounted.current) return;
      // Only show error if we have nothing to display
      setWebsites(prev => {
        if (prev.length === 0) setLoadError('Failed to load your websites. Please try again.');
        return prev;
      });
    } finally {
      if (isMounted.current) {
        setIsFirstLoad(false);
        setIsRefreshing(false);
      }
    }
  }, [userId]);

  // On mount / userId change: show cache immediately, then refresh in background
  useEffect(() => {
    const cached = userId ? readCache(userId) : null;
    if (cached && cached.length > 0) {
      setWebsites(cached);
      setIsFirstLoad(false);
      // Background silent refresh
      fetchWebsites({ silent: true });
    } else {
      // Nothing cached — show skeletons while fetching
      fetchWebsites({ silent: false });
    }
  }, [userId, fetchWebsites]);

  const handleManualRefresh = () => fetchWebsites({ silent: false });

  const handleCreateWebsite = async () => {
    if (!newSiteName.trim() || !userId) return;
    setIsCreating(true); setCreateError(null); setNetlifyStatus('Creating your site...');
    let netlifyData = null; let netlifyProvisionSuccess = false;
    try {
      setNetlifyStatus('🌐 Registering subdomain on Netlify...');
      try {
        netlifyData = await createNetlifySite(newSiteName.trim(), githubRepo.trim());
        netlifyProvisionSuccess = true;
        setNetlifyStatus(`✅ Subdomain ready: ${netlifyData.subdomain}`);
      } catch (netlifyErr) {
        console.warn('Netlify provisioning failed (non-fatal):', netlifyErr.message);
        setNetlifyStatus('⚠️ Subdomain provisioning skipped — continuing...');
      }

      const insertPayload = {
        name: newSiteName.trim(), type: newSiteType, user_id: userId, layout_data: {},
        thumbnail_url: null, banner_url: null,
        ...(netlifyData && {
          netlify_site_id: netlifyData.netlify_site_id,
          netlify_site_name: netlifyData.netlify_site_name,
          netlify_subdomain: netlifyData.subdomain,
          netlify_url: netlifyData.netlify_url,
          netlify_slug: netlifyData.netlify_slug,
        }),
      };

      const { data, error } = await supabase.from('websites_1772750000000').insert(insertPayload).select().single();
      if (error) throw error;

      const updated = [data, ...websites];
      setWebsites(updated);
      writeCache(userId, updated);

      setCreationToast({ success: netlifyProvisionSuccess, subdomain: netlifyData?.subdomain || null });
      closeCreateModal();
      if (onOpenWebsite) onOpenWebsite(data.id, data.name);
    } catch (err) {
      setCreateError(err.message || 'An unexpected error occurred.');
      setNetlifyStatus('');
    } finally {
      setIsCreating(false);
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false); setNewSiteName(''); setNewSiteType('website');
    setGithubRepo(''); setCreateError(null); setNetlifyStatus('');
  };

  const handleDeleteWebsite = async (id) => {
    setIsDeleting(true);
    try {
      const site = websites.find((w) => w.id === id);
      if (site?.netlify_site_id) {
        try { await deleteNetlifySite(site.netlify_site_id); } catch (e) { console.warn('Netlify delete failed:', e.message); }
      }
      const { error } = await supabase.from('websites_1772750000000').delete().eq('id', id);
      if (error) throw error;
      const updated = websites.filter((w) => w.id !== id);
      setWebsites(updated);
      writeCache(userId, updated);
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete website:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDuplicateWebsite = async (website) => {
    if (!userId) return;
    const dupName = `${website.name} Copy`;
    let netlifyData = null; let netlifySuccess = false;
    try {
      try { netlifyData = await createNetlifySite(dupName); netlifySuccess = true; } catch (e) { console.warn('Netlify dup failed:', e.message); }
      const { data, error } = await supabase.from('websites_1772750000000').insert({
        name: dupName, type: website.type, user_id: userId,
        layout_data: website.layout_data, thumbnail_url: website.thumbnail_url, banner_url: website.banner_url,
        ...(netlifyData && {
          netlify_site_id: netlifyData.netlify_site_id, netlify_site_name: netlifyData.netlify_site_name,
          netlify_subdomain: netlifyData.subdomain, netlify_url: netlifyData.netlify_url, netlify_slug: netlifyData.netlify_slug,
        }),
      }).select().single();
      if (error) throw error;
      const updated = [data, ...websites];
      setWebsites(updated);
      writeCache(userId, updated);
      setCreationToast({ success: netlifySuccess, subdomain: netlifyData?.subdomain || null });
    } catch (err) {
      console.error('Failed to duplicate website:', err);
    }
  };

  const handleEdit = (site) => { if (onOpenWebsite) onOpenWebsite(site.id, site.name); };

  const previewSubdomain = newSiteName.trim() ? `${nameToNetlifySlug(newSiteName.trim())}.sidehustlr.co.za` : null;

  const filteredWebsites = websites.filter(
    (s) => (filterType === 'all' || s.type === filterType) &&
            s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Skeleton count — show 3 placeholder cards on first load
  const SKELETON_COUNT = 3;

  return (
    <div className="flex flex-col h-screen w-full bg-[#0a0a0a] font-sans overflow-hidden">
      <TopNav
        view="sites" activeWebsiteName={activeWebsiteName}
        onOpenSites={() => {}} onOpenBuilder={onOpenBuilder} onOpenStore={onOpenStore}
        menuStyles={menuStyles} onMenuStylesChange={onMenuStylesChange}
        onUndo={() => {}} onRedo={() => {}} canUndo={false} canRedo={false}
      />

      <div className="flex-1 bg-[#0a0a0a] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-[#111318] border-b border-[#2c313a] px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <h1 className="text-xl font-bold text-white">My Sites &amp; Funnels</h1>
            <p className="text-sm text-gray-500">Each site gets its own subdomain on sidehustlr.co.za</p>
          </div>
          <div className="flex items-center space-x-3">
            <SyncIndicator isRefreshing={isRefreshing} />
            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="p-2.5 bg-[#1e2228] border border-[#2c313a] text-gray-400 hover:text-white rounded-xl transition-all disabled:opacity-40"
              title="Refresh"
            >
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#c8f542] hover:bg-[#d4ff50] text-black px-5 py-2.5 rounded-xl font-black text-sm flex items-center space-x-2 transition-all shadow-lg shadow-[#c8f542]/20"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" /><span>Create New</span>
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-[#111318] border-b border-[#2c313a] px-6 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search websites..."
                className="pl-10 pr-4 py-2 bg-[#1e2228] border border-[#2c313a] rounded-xl text-sm outline-none focus:border-[#c8f542]/50 transition-all w-64 text-white placeholder-gray-600" />
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-500" />
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                className="py-2 px-4 bg-[#1e2228] border border-[#2c313a] rounded-xl text-sm outline-none focus:border-[#c8f542]/50 cursor-pointer text-white">
                <option value="all">All Types</option>
                <option value="website">Websites</option>
                <option value="funnel">Funnels</option>
              </select>
            </div>
          </div>
          <div className="flex items-center space-x-2 bg-[#1e2228] p-1 rounded-xl border border-[#2c313a]">
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-[#c8f542] text-black' : 'text-gray-500 hover:text-gray-300'}`}>
              <SafeIcon icon={FiGrid} className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-[#c8f542] text-black' : 'text-gray-500 hover:text-gray-300'}`}>
              <SafeIcon icon={FiList} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {loadError && (
            <div className="flex flex-col items-center justify-center h-64 space-y-4 text-center">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20">
                <SafeIcon icon={FiAlertCircle} className="w-7 h-7 text-red-400" />
              </div>
              <p className="font-bold text-gray-400 text-sm">{loadError}</p>
              <button onClick={handleManualRefresh} className="px-5 py-2 bg-[#c8f542] text-black font-black rounded-xl text-sm hover:bg-[#d4ff50] transition-colors">
                Try Again
              </button>
            </div>
          )}

          {/* Skeleton on very first load (no cache) */}
          {!loadError && isFirstLoad && viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          )}
          {!loadError && isFirstLoad && viewMode === 'list' && (
            <div className="bg-[#111318] rounded-xl border border-[#2c313a] overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#1e2228] border-b border-[#2c313a]">
                  <tr>{['Preview', 'Name', 'Type', 'Netlify Status', 'Last Updated', 'Actions'].map((h) => (
                    <th key={h} className="text-left text-[10px] font-black text-gray-500 uppercase tracking-wider px-6 py-4">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>{Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonRow key={i} />)}</tbody>
              </table>
            </div>
          )}

          {/* Actual content */}
          {!loadError && !isFirstLoad && (
            <>
              {filteredWebsites.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 space-y-4 text-center">
                  <div className="w-20 h-20 bg-[#1e2228] rounded-full flex items-center justify-center border border-[#2c313a]">
                    <SafeIcon icon={FiGrid} className="w-8 h-8 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-black text-gray-500 uppercase tracking-wider text-sm">
                      {searchQuery || filterType !== 'all' ? 'No results found' : 'No websites yet'}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {searchQuery || filterType !== 'all' ? 'Try adjusting your search or filter' : 'Create your first website to get started'}
                    </p>
                  </div>
                  {!searchQuery && filterType === 'all' && (
                    <button onClick={() => setShowCreateModal(true)} className="mt-2 px-6 py-2.5 bg-[#c8f542] text-black font-black rounded-xl text-sm hover:bg-[#d4ff50] transition-colors">
                      Create Website
                    </button>
                  )}
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredWebsites.map((site) => (
                    <SiteThumbnail key={site.id} site={site} onEdit={handleEdit} onDuplicate={handleDuplicateWebsite} onDelete={setDeleteConfirm} />
                  ))}
                </div>
              ) : (
                <div className="bg-[#111318] rounded-xl border border-[#2c313a] overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#1e2228] border-b border-[#2c313a]">
                      <tr>{['Preview', 'Name', 'Type', 'Netlify Status', 'Last Updated', 'Actions'].map((h) => (
                        <th key={h} className="text-left text-[10px] font-black text-gray-500 uppercase tracking-wider px-6 py-4">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {filteredWebsites.map((site) => (
                        <ListRow key={site.id} site={site} onEdit={handleEdit} onDuplicate={handleDuplicateWebsite} onDelete={setDeleteConfirm} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Create Modal ── */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
            onClick={closeCreateModal}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="bg-[#111318] rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden border border-[#2c313a]"
              onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-[#2c313a]">
                <h3 className="font-black text-white text-xl uppercase tracking-tight">Create New</h3>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-0.5">Your site gets a free subdomain on sidehustlr.co.za</p>
              </div>
              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                <AnimatePresence>
                  {createError && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                      className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl p-4 flex items-start space-x-3 text-sm">
                      <SafeIcon icon={FiAlertCircle} className="w-5 h-5 shrink-0 mt-0.5" /><p>{createError}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div>
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-3">Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    {['website', 'funnel'].map((type) => (
                      <button key={type} onClick={() => setNewSiteType(type)}
                        className={`p-4 rounded-2xl border-2 transition-all ${newSiteType === type ? 'border-[#c8f542] bg-[#c8f542]/10' : 'border-[#2c313a] hover:border-[#383e49] bg-[#1e2228]'}`}>
                        <SafeIcon icon={type === 'website' ? FiGrid : FiExternalLink} className={`w-6 h-6 mx-auto mb-2 ${newSiteType === type ? 'text-[#c8f542]' : 'text-gray-500'}`} />
                        <span className={`text-sm font-black uppercase tracking-wider ${newSiteType === type ? 'text-[#c8f542]' : 'text-gray-500'}`}>{type}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-3">Name</label>
                  <input type="text" value={newSiteName} onChange={(e) => { setNewSiteName(e.target.value); if (createError) setCreateError(null); }}
                    placeholder={`Enter ${newSiteType} name...`} autoFocus
                    className="w-full p-4 bg-[#1e2228] border border-[#2c313a] rounded-xl outline-none focus:border-[#c8f542]/50 transition-all font-bold text-white placeholder-gray-600"
                    onKeyDown={(e) => e.key === 'Enter' && newSiteName.trim() && handleCreateWebsite()} />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-3">Link GitHub Repository (Optional)</label>
                  <div className="relative">
                    <SafeIcon icon={FiGithub} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input type="text" value={githubRepo} onChange={(e) => setGithubRepo(e.target.value)} placeholder="e.g. username/my-repo"
                      className="w-full pl-11 pr-4 py-4 bg-[#1e2228] border border-[#2c313a] rounded-xl outline-none focus:border-[#c8f542]/50 transition-all font-bold text-white placeholder-gray-600" />
                  </div>
                  <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">Connects your site to a GitHub repository for automatic Netlify builds.</p>
                </div>
                {previewSubdomain && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="bg-[#c8f542]/10 border border-[#c8f542]/30 rounded-xl p-4 flex items-center space-x-3">
                    <div className="w-8 h-8 bg-[#c8f542]/20 rounded-lg flex items-center justify-center shrink-0">
                      <SafeIcon icon={FiGlobe} className="w-4 h-4 text-[#c8f542]" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-[#c8f542]/70 uppercase tracking-widest">Your subdomain will be</p>
                      <p className="text-sm font-black text-[#c8f542]">{previewSubdomain}</p>
                    </div>
                  </motion.div>
                )}
                {isCreating && netlifyStatus && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="flex items-center space-x-2 text-sm text-gray-400 bg-[#1e2228] rounded-xl px-4 py-3 border border-[#2c313a]">
                    <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin text-[#c8f542]" />
                    <span>{netlifyStatus}</span>
                  </motion.div>
                )}
              </div>
              <div className="p-6 bg-[#0a0a0a] border-t border-[#2c313a] flex space-x-4">
                <button onClick={closeCreateModal} className="flex-1 py-4 font-black text-gray-500 hover:text-gray-300 uppercase tracking-widest text-xs transition-colors">Cancel</button>
                <button onClick={handleCreateWebsite} disabled={!newSiteName.trim() || isCreating}
                  className="flex-[2] py-4 bg-[#c8f542] text-black font-black uppercase tracking-widest text-xs rounded-2xl shadow-xl hover:bg-[#d4ff50] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2">
                  {isCreating
                    ? <><SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /><span>Creating...</span></>
                    : <><SafeIcon icon={FiPlus} className="w-4 h-4" /><span>Create {newSiteType === 'website' ? 'Website' : 'Funnel'}</span></>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete Confirmation ── */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
            onClick={() => setDeleteConfirm(null)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="bg-[#111318] rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden border border-[#2c313a]"
              onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-[#2c313a] flex items-center space-x-4">
                <div className="p-3 bg-red-500/10 rounded-xl">
                  <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="font-black text-white">Delete {deleteConfirm.type === 'website' ? 'Website' : 'Funnel'}?</h3>
                  <p className="text-xs text-gray-500">This will also remove the Netlify subdomain.</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-400">
                  Are you sure you want to delete <strong className="text-white">{deleteConfirm.name}</strong>? All data and the Netlify subdomain will be permanently removed.
                </p>
              </div>
              <div className="p-6 bg-[#0a0a0a] border-t border-[#2c313a] flex space-x-4">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-4 font-bold text-gray-500 hover:text-gray-300 rounded-xl transition-colors">Cancel</button>
                <button onClick={() => handleDeleteWebsite(deleteConfirm.id)} disabled={isDeleting}
                  className="flex-1 py-4 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-colors flex items-center justify-center space-x-2">
                  {isDeleting
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <><SafeIcon icon={FiTrash2} className="w-4 h-4" /><span>Delete</span></>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Creation Toast ── */}
      <AnimatePresence>
        {creationToast && <CreationToast result={creationToast} onClose={() => setCreationToast(null)} />}
      </AnimatePresence>
    </div>
  );
};

export default SitesFunnelsDashboard;